import { KalturaMediaAsset } from '@kaltura-ott/ott-client-types/api/types';

import {
  AssetPresentationRatioEnum,
  DisplayType,
  KuxFilteredCategoriesDTO,
  KuxMenuItem,
  TypeOfApplicationLink,
  UIDAPresentationTypeEnum,
} from '../interfaces';

import { categoryTreeItemsMap } from './category.tree.map';
import { getRatioByType } from './image-types';
import { PREDEFIENED_REFERENCES } from './utils/constants';
import { toKuxImages, KuxImages } from './utils/image';
import { KalturaCategoryMetas, normalizeCategoryMetas } from './utils/meta';
import { KalturaCategoryTags, normalizeCategoryTags } from './utils/tags';

function getGridLink({
  inAppLink,
  presentationType: railPresentationType,
  targetId,
  typeOfAppLink,
}: {
  typeOfAppLink: TypeOfApplicationLink;
  inAppLink: string;
  presentationType?: UIDAPresentationTypeEnum;
  targetId?: number | string;
}) {
  if (railPresentationType) {
    const linkedCategory = categoryTreeItemsMap.get(String(targetId));
    /**
     * if category is not present in map, this is most likely referenceId is kaltura channel id and
     * we use 'collection' subtype as default in this case
     */
    const presentationType = linkedCategory?.subtype || UIDAPresentationTypeEnum.collection;
    const presentationTypeUrl =
      presentationType === UIDAPresentationTypeEnum.series_catch_up ? 'series-catch-up' : String(presentationType);

    return `/${typeOfAppLink}/${presentationTypeUrl}/${inAppLink || PREDEFIENED_REFERENCES.grid}/${targetId}`;
  }

  return `/${typeOfAppLink}/${inAppLink || PREDEFIENED_REFERENCES.grid}/${targetId}`;
}

/**
 * Generate readable Link
 *
 * @param typeOfAppLink: TypeOfApplicationLink
 * @param inAppLink: inAppLink
 * @param targetId
 */
export const generateMenuLink = (
  typeOfAppLink: TypeOfApplicationLink,
  inAppLink: string,
  targetId?: number | string,
  presentationType?: UIDAPresentationTypeEnum,
): string => {
  switch (typeOfAppLink) {
    case TypeOfApplicationLink.page:
    case TypeOfApplicationLink.asset:
      return `/${typeOfAppLink}/${inAppLink}`;
    case TypeOfApplicationLink.grid:
      return getGridLink({
        inAppLink,
        presentationType,
        targetId,
        typeOfAppLink,
      });
    case TypeOfApplicationLink.external:
      return inAppLink;
    default:
      console.warn('TypeOfApplicationLink is not defined', typeOfAppLink);
      return inAppLink;
  }
};

export const generateMenuItem = ({
  typeOfAppLink,
  inAppLink,
  id = 0,
  referenceId = '',
  name = '',
  image,
  images = {},
  attachedCollectionId,
  displayType,
  presentationType,
}: {
  typeOfAppLink: TypeOfApplicationLink;
  inAppLink: string;
  id?: number;
  referenceId?: string;
  name?: string;
  image?: string;
  images?: KuxImages;
  attachedCollectionId?: number;
  displayType?: DisplayType;
  presentationType?: UIDAPresentationTypeEnum;
}): KuxMenuItem => ({
  id,
  referenceId,
  name,
  inAppLink,
  typeOfAppLink,
  link: generateMenuLink(typeOfAppLink, inAppLink, attachedCollectionId || referenceId, presentationType),
  image,
  images,
  attachedCollectionId,
  displayType,
});

export const parseMenuItemFromCategory = (
  categoryItem: KuxFilteredCategoriesDTO,
  { includePresentationType }: { includePresentationType?: boolean } = {},
): KuxMenuItem | undefined => {
  if (categoryItem.link.typeOfAppLink && categoryItem.link.inAppLink) {
    return generateMenuItem({
      typeOfAppLink: categoryItem.link.typeOfAppLink,
      inAppLink: categoryItem.link.inAppLink,
      id: categoryItem.id,
      referenceId: categoryItem.referenceId,
      name: categoryItem.name,
      image:
        categoryItem.images?.find(
          ({ imageTypeId }) => imageTypeId === categoryItem.railImageProperty?.assetPresentationImageType,
        )?.url || categoryItem.images[0]?.url,
      images: categoryItem.railImageProperty?.assetPresentationImageType
        ? {
            [categoryItem.railImageProperty?.assetPresentationImageType]: {
              ratio: categoryItem.railImageProperty.assetPresentationImageType
                ? getRatioByType(categoryItem.railImageProperty.assetPresentationImageType) ||
                  AssetPresentationRatioEnum['1x1']
                : AssetPresentationRatioEnum['1x1'],
              url:
                categoryItem.images?.find(
                  ({ imageTypeId }) => imageTypeId === categoryItem.railImageProperty?.assetPresentationImageType,
                )?.url || '',
            },
          }
        : {},
      attachedCollectionId: categoryItem.link.attachedCollectionId,
      displayType: categoryItem.link.displayType,
      ...(includePresentationType ? { presentationType: categoryItem.subtype } : undefined),
    });
  }
};

export const parseMenuItem = (
  asset: KalturaMediaAsset,
  presentationType?: UIDAPresentationTypeEnum,
): KuxMenuItem | undefined => {
  if (!asset?.metas || !asset?.tags) return;
  const metas = normalizeCategoryMetas(asset.metas as KalturaCategoryMetas);
  const tags = normalizeCategoryTags(asset.tags as KalturaCategoryTags);
  if (metas['in_app_link'] && tags['type_of_app_link']) {
    return generateMenuItem({
      typeOfAppLink: tags['type_of_app_link'] as TypeOfApplicationLink,
      inAppLink: metas['in_app_link'] as string,
      id: asset.id,
      name: asset.name,
      image: asset.images[0]?.url,
      images: toKuxImages(asset.images),
      attachedCollectionId: metas['attached_collection_id'] as number,
      displayType: (tags['display_type'] as DisplayType) || DisplayType.text,
      presentationType,
    });
  }
};
